/* eslint-disable @typescript-eslint/no-explicit-any */
import './brokerage-tabs.scss';

import { Box, Container, Stack, Tab, Tabs, Typography } from '@mui/material';
import {
  CircularLoader,
  DialogLayout,
  NewConvergeButton,
} from '../../../../ui/src/presentation';
import {
  ContactType,
  LicensePaginationSort,
  LicenseSortKey,
  SortOrder,
  UserGroup,
  useGetBrokerage,
  useUpsertBrokerageMutation,
} from '@converge/graphql';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import React, { useCallback, useEffect, useState } from 'react';
import { capitalizeFirstLetter, scrollToFirstError } from '@converge/utils';
import { useConvergeNavigation, useConvergeSnackBar } from '../../contexts';

import { ApolloError } from '@apollo/client';
import BrokageFormAccordions from './accordion.form-container';
import { BrokerBrokerageHeader } from '../entity/broker-agencies/broker-form/broker-brokerage-header';
import { BrokerageNoteContainer } from './brokerage-note-container';
import { CreateBrokerFieldsSchema } from './brokerage.validation.schema';
import { CreateBrokerageField } from '@converge/data';
import { CreateBrokerageFields } from './commission-container';
import { IOffices } from './accordion.form-container';
import { ReactiveBrokerBrokerage } from './reactive-broker-brokerage';
import Warning from '@mui/icons-material/Warning';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export type BrokerageParams = {
  brokerageUuid: string;
};
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          className={`${
            value === 1 ? 'broker-tabs-wrapper' : 'broker-tabs-wrapper2'
          }`}
          sx={{ p: 3 }}
        >
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const BrokerageTabs = () => {
  interface CreateBrokerageFields {
    [CreateBrokerageField.CompanyName]: string | null;
    [CreateBrokerageField.DbaName]: string | null;
    [CreateBrokerageField.Domain]: string;
    [CreateBrokerageField.GlobalParentCompany]: undefined | null | string;
    [CreateBrokerageField.PartnerEntity]: undefined | null | string;
    [CreateBrokerageField.Fein]: string;
    [CreateBrokerageField.Npn]: string;
    [CreateBrokerageField.ProgramUuid]: string | null | undefined;
    [CreateBrokerageField.CoverageRegionUuid]: string;
    [CreateBrokerageField.PrimaryStreet1]: string;
    [CreateBrokerageField.PrimaryStreet2]: string;
    [CreateBrokerageField.PrimaryCity]: string;
    [CreateBrokerageField.PrimaryRegionUuid]: string;
    [CreateBrokerageField.PrimaryCountryUuid]: string;
    [CreateBrokerageField.PrimaryPostalCode]: string;
    [CreateBrokerageField.Street1]: string;
    [CreateBrokerageField.Street2]: string;
    [CreateBrokerageField.City]: string;
    [CreateBrokerageField.RegionUuid]: string;
    [CreateBrokerageField.CountryUuid]: string;
    [CreateBrokerageField.PostalCode]: string;
    [CreateBrokerageField.PrimaryPhoneNumber]: string;
    [CreateBrokerageField.PrimaryPhoneNumberExtn]: string;
    [CreateBrokerageField.Digital]: number | undefined | null | string;
    [CreateBrokerageField.Wholesale]: number | undefined | null | string;
    [CreateBrokerageField.BusinessDevelopmentManager]:
      | undefined
      | null
      | string;
    [CreateBrokerageField.Underwriter]: undefined | null | string;
    [CreateBrokerageField.UwRegion]: undefined | null | string;
    [CreateBrokerageField.BdmRegion]: undefined | null | string;
    [CreateBrokerageField.PrimaryContactUuid]: undefined | null | string;
    [CreateBrokerageField.PrimaryContactEmail]: undefined | null | string;
  }

  const [value, setValue] = useState(0);
  const [open, setOpen] = useState(false);
  const [firstRender, setFirstRender] = useState(true);
  const [reactiveOpen, setReactiveOpen] = useState(false);
  const [hasInitializedForm, setHasInitializedForm] = useState(false);
  const [licenseSort, setLicenseSort] = useState<LicensePaginationSort>({
    key: LicenseSortKey.Type,
    order: SortOrder.Asc,
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { brokerageUuid } = useParams<keyof BrokerageParams>();

  const { loading: brokerageLoading, brokerages } = useGetBrokerage({
    uuid: brokerageUuid,
    enabled: Boolean(brokerageUuid),
    licenseSort: licenseSort,
  });

  useEffect(() => {
    if (brokerages[0]) {
      setFirstRender(false);
    }
  }, [brokerages]);
  const transformOffices = (offices: any[]): IOffices[] => {
    return (offices || []).map((office) => ({
      ...office,
      node: {
        ...office.node,
        bdmTerritories: office.node.bdmTerritories
          ? {
              ...office.node.bdmTerritories,
              __typename: 'BdmTerritoryEdge',
              edges: office.node.bdmTerritories.edges.map((edge: any) => ({
                ...edge,
                __typename: 'BdmTerritory',
              })),
            }
          : null,
      },
    }));
  };

  const brokerage = brokerages[0];
  const navigate = useConvergeNavigation();
  const { notify } = useConvergeSnackBar();
  const formMethods = useForm<CreateBrokerageFields>({
    defaultValues: {
      [CreateBrokerageField.PrimaryCountryUuid]:
        'bb3ca70e-1df7-40a5-966e-f82ebc541cd3',
      [CreateBrokerageField.CountryUuid]:
        'bb3ca70e-1df7-40a5-966e-f82ebc541cd3',
    },
    mode: 'all',
    resolver: yupResolver(CreateBrokerFieldsSchema),
  });

  const BusinessDevelopmentManagerUuid = brokerage?.user?.edges?.filter(
    (bDMuser) => bDMuser?.node?.type === UserGroup.Bdm
  )[0]?.node?.uuid;

  const underwriterUuid = brokerage?.user?.edges?.filter(
    (underwriter) => underwriter?.node?.type === UserGroup.Underwriter
  )[0]?.node?.uuid;

  const primaryPerson = brokerage?.person?.edges.filter(
    (person) => person?.node?.primaryContact === true
  )[0];

  const [isSameAddress, setIsSameAddress] = useState(
    brokerageUuid &&
      brokerage?.billingStreet1 === brokerage?.primaryStreet1 &&
      brokerage?.billingStreet2 === brokerage?.primaryStreet2 &&
      brokerage?.billingCity === brokerage?.primaryCity
      ? true
      : false
  );
  const [isLicenseBrokerage, setIsLicenseBrokerage] = useState(true);

  const handleLicenseSortChange = (sortModel: LicensePaginationSort) => {
    setLicenseSort(sortModel);
  };
  const { upsertEntity, loading } = useUpsertBrokerageMutation();
  useEffect(() => {
    setIsSameAddress(
      brokerageUuid &&
        brokerage?.billingStreet1 === brokerage?.primaryStreet1 &&
        brokerage?.billingStreet2 === brokerage?.primaryStreet2 &&
        brokerage?.billingCity === brokerage?.primaryCity
        ? true
        : false
    );
  }, [
    brokerageUuid,
    brokerage?.primaryStreet1,
    brokerage?.billingStreet1,
    brokerage?.billingStreet2,
    brokerage?.primaryStreet2,
    brokerage?.billingCity,
    brokerage?.primaryCity,
  ]);

  useEffect(() => {
    // Only initialize form once when brokerage data is first loaded
    // This prevents form reset during refetch operations (like license sorting)
    if (brokerage && !brokerageLoading && !hasInitializedForm) {
      formMethods.reset({
        [CreateBrokerageField.CompanyName]: brokerage?.brokerageName,
        [CreateBrokerageField.DbaName]: brokerage?.dbaName || '',
        [CreateBrokerageField.Domain]: brokerage?.domainName,
        [CreateBrokerageField.ProgramUuid]: brokerage?.programUuid,
        [CreateBrokerageField.Fein]: brokerage?.fein,
        [CreateBrokerageField.GlobalParentCompany]: brokerage?.parentUuid,
        [CreateBrokerageField.PartnerEntity]: brokerage?.partnerUuid,
        [CreateBrokerageField.CoverageRegionUuid]: brokerage?.uwRegion,
        [CreateBrokerageField.BdmRegion]: brokerage?.BdmRegion,
        [CreateBrokerageField.PrimaryRegionUuid]: brokerage?.primaryStateUuid,
        [CreateBrokerageField.PrimaryCity]: brokerage?.primaryCity,
        [CreateBrokerageField.PrimaryStreet1]: brokerage?.primaryStreet1,
        [CreateBrokerageField.PrimaryStreet2]: brokerage?.primaryStreet2,
        [CreateBrokerageField.PrimaryPostalCode]: brokerage?.primaryPostalCode,
        [CreateBrokerageField.Wholesale]: brokerage?.wholesaleCommission,
        [CreateBrokerageField.Digital]: brokerage?.digitalCommission,
        [CreateBrokerageField.PrimaryCountryUuid]: brokerage?.primaryCountry,
        [CreateBrokerageField.PrimaryPhoneNumber]: brokerage?.primaryPhoneNo
          ? brokerage?.primaryPhoneNo.split('x')[0]
          : '',
        [CreateBrokerageField.PrimaryPhoneNumberExtn]: brokerage?.primaryPhoneNo
          ? brokerage?.primaryPhoneNo.split('x')[1]
          : '',
        [CreateBrokerageField.RegionUuid]: brokerage?.billingStateUuid,
        [CreateBrokerageField.City]: brokerage?.billingCity,
        [CreateBrokerageField.Street1]: brokerage?.billingStreet1,
        [CreateBrokerageField.Street2]: brokerage?.billingStreet2,
        [CreateBrokerageField.PostalCode]: brokerage?.billingPostalCode,
        [CreateBrokerageField.CountryUuid]: brokerage?.billingCountry,
        [CreateBrokerageField.BusinessDevelopmentManager]:
          BusinessDevelopmentManagerUuid ?? '',
        [CreateBrokerageField.UwRegion]: brokerage?.uWRegion || '',
        [CreateBrokerageField.Underwriter]: underwriterUuid || '',
        [CreateBrokerageField.PrimaryContactUuid]:
          primaryPerson?.node?.uuid || '',
        [CreateBrokerageField.PrimaryContactEmail]:
          primaryPerson?.node?.contacts?.edges?.filter(
            (person) => person?.node?.type === ContactType.Email
          )[0]?.node?.value || '',
      });
      setHasInitializedForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMethods, brokerage, brokerageLoading, hasInitializedForm]);
  const { isDirty, dirtyFields } = formMethods.formState;

  const onSubmitHandlers = useCallback(
    (): SubmitHandler<CreateBrokerageFields> => async (fields) => {
      let brokerResponse, brokerResponseErrors;
      const { primaryPhoneNumber, primaryPhoneNumberExtn } = fields;
      const brokerageData = {
        addresses: [],
        licensedBroker: isLicenseBrokerage,
        aliases: [],
      } as any;
      if (dirtyFields[CreateBrokerageField.CompanyName]) {
        brokerageData.aliases.push({
          aliasUuid: brokerage ? brokerage.brokerageNameUuid : null,
          name: fields?.companyName?.trim(),
          type: 'LEGAL_NAME',
        });
      }

      if (dirtyFields[CreateBrokerageField.DbaName]) {
        brokerageData.aliases.push({
          aliasUuid: brokerage ? brokerage.dbaUuid : null,
          name:
            fields?.dbaName?.trim() && fields?.dbaName?.trim().length > 0
              ? fields?.dbaName?.trim()
              : null,
          type: 'TRADING_NAME',
        });
      }
      const updateBrokerageData = (
        fieldName: string,
        formFieldName: CreateBrokerageField,
        fieldValue: any
      ) => {
        if (dirtyFields[formFieldName]) {
          brokerageData[fieldName] = fieldValue;
        }
      };

      const updateBrokeragAddresseData = (
        isPrimary: boolean,
        fieldName: string,
        formFieldName: CreateBrokerageField,
        fieldValue: any
      ) => {
        if (
          (dirtyFields[formFieldName] && isPrimary) ||
          formFieldName === CreateBrokerageField.PrimaryCountryUuid
        ) {
          const corporateAddressIndex = brokerageData.addresses.findIndex(
            (address: any) => {
              return address.type === 'CORPORATE';
            }
          );
          if (corporateAddressIndex > -1) {
            brokerageData.addresses[corporateAddressIndex][fieldName] =
              fieldValue;
            brokerageData.addresses[corporateAddressIndex]['type'] =
              'CORPORATE';
            brokerageData.addresses[corporateAddressIndex]['addressUuid'] =
              brokerageUuid ? brokerage.primaryAddressUuid : null;
          } else {
            brokerageData.addresses.push({
              [fieldName]: fieldValue,
              type: 'CORPORATE',
              addressUuid: brokerageUuid ? brokerage.primaryAddressUuid : null,
            });
          }
        }

        if (
          (dirtyFields[formFieldName] && !isPrimary) ||
          !brokerageUuid ||
          isSameAddress
        ) {
          const billingAddressIndex = brokerageData.addresses.findIndex(
            (address: any) => {
              return address.type === 'BILLING';
            }
          );
          if (billingAddressIndex > -1) {
            brokerageData.addresses[billingAddressIndex][fieldName] =
              fieldValue;
            brokerageData.addresses[billingAddressIndex]['type'] = 'BILLING';
            brokerageData.addresses[billingAddressIndex]['addressUuid'] =
              brokerageUuid ? brokerage.billingAddressUuid : null;
          } else {
            brokerageData.addresses.push({
              [fieldName]: fieldValue,
              type: 'BILLING',
              addressUuid: brokerageUuid ? brokerage.billingAddressUuid : null,
            });
          }
        }
      };

      if (brokerageUuid) {
        brokerageData.entityUuid = brokerageUuid;
      } else {
        brokerageData.appendUwTerritories = true;
        brokerageData.agency = true;
        brokerageData.status = 'ACTIVE';
      }

      if (dirtyFields.primaryPhoneNumber) {
        brokerageData.contacts = [
          {
            contactUuid: brokerageUuid ? brokerage.primaryPhoneUuid : null,
            type: ContactType.Phone,
            value:
              primaryPhoneNumber && primaryPhoneNumberExtn
                ? `${primaryPhoneNumber}x${primaryPhoneNumberExtn}`
                : primaryPhoneNumber || '',
          },
        ];
      }

      const handleBrokerageAddressUpdates = () => {
        updateBrokeragAddresseData(
          true,
          'street1',
          CreateBrokerageField.PrimaryStreet1,
          fields.primaryStreet1
        );

        updateBrokeragAddresseData(
          true,
          'street2',
          CreateBrokerageField.PrimaryStreet2,
          fields.primaryStreet2
        );

        updateBrokeragAddresseData(
          true,
          'city',
          CreateBrokerageField.PrimaryCity,
          fields.primaryCity
        );
        updateBrokeragAddresseData(
          true,
          'postalCode',
          CreateBrokerageField.PrimaryPostalCode,
          fields.primaryPostalCode
        );

        updateBrokeragAddresseData(
          true,
          'regionUuid',
          CreateBrokerageField.PrimaryRegionUuid,
          fields.primaryRegionUuid
        );

        updateBrokeragAddresseData(
          true,
          'countryUuid',
          CreateBrokerageField.PrimaryCountryUuid,
          fields.primaryCountryUuid
        );

        updateBrokeragAddresseData(
          false,
          'street1',
          CreateBrokerageField.Street1,
          isSameAddress ? fields.primaryStreet1 : fields.street1
        );
        updateBrokeragAddresseData(
          false,
          'street2',
          CreateBrokerageField.Street2,
          isSameAddress ? fields.primaryStreet2 : fields.street2
        );

        updateBrokeragAddresseData(
          false,
          'city',
          CreateBrokerageField.City,
          isSameAddress ? fields.primaryCity : fields.city
        );

        updateBrokeragAddresseData(
          false,
          'postalCode',
          CreateBrokerageField.PostalCode,
          isSameAddress ? fields.primaryPostalCode : fields.postalCode
        );

        updateBrokeragAddresseData(
          false,
          'countryUuid',
          CreateBrokerageField.CountryUuid,
          isSameAddress ? fields.primaryCountryUuid : fields.countryUuid
        );
      };

      handleBrokerageAddressUpdates();

      updateBrokerageData('npn', CreateBrokerageField.Npn, fields?.npn);
      updateBrokerageData(
        'domain',
        CreateBrokerageField.Domain,
        fields?.domain
      );

      updateBrokerageData('fein', CreateBrokerageField.Fein, fields?.fein);

      updateBrokerageData(
        'programUuid',
        CreateBrokerageField.ProgramUuid,
        fields?.programUuid
      );

      updateBrokerageData(
        'parentEntityUuid',
        CreateBrokerageField.GlobalParentCompany,
        fields?.globalParentCompany ? fields?.globalParentCompany : null
      );
      updateBrokerageData(
        'partnerEntityUuid',
        CreateBrokerageField.PartnerEntity,
        fields?.partnerEntity ? fields?.partnerEntity : null
      );

      updateBrokerageData(
        'uwTerritoryUuids',
        CreateBrokerageField.CoverageRegionUuid,
        fields.convergeRegion ? [fields.convergeRegion] : null
      );

      updateBrokerageData(
        'wholesaleCommission',
        CreateBrokerageField.Wholesale,
        fields?.wholesale && +fields?.wholesale / 100
      );

      updateBrokerageData(
        'digitalCommission',
        CreateBrokerageField.Digital,
        fields?.digital && +fields?.digital / 100
      );

      updateBrokerageData(
        'bdmUserUuid',
        CreateBrokerageField.BusinessDevelopmentManager,

        fields?.bdmUserUuid && fields?.bdmUserUuid.length
          ? fields?.bdmUserUuid
          : 'null'
      );
      updateBrokerageData(
        'bdmTerritoryUuids',
        CreateBrokerageField.BdmRegion,
        fields?.bdmTerritoryUuids && fields?.bdmTerritoryUuids.length
          ? [fields?.bdmTerritoryUuids]
          : []
      );
      updateBrokerageData(
        'uwTerritoryUuids',
        CreateBrokerageField.UwRegion,
        fields?.uwTerritoryUuids && fields?.uwTerritoryUuids.length
          ? [fields?.uwTerritoryUuids]
          : []
      );
      updateBrokerageData(
        'underwriterUserUuid',
        CreateBrokerageField.Underwriter,
        fields?.underwriterUserUuid && fields?.underwriterUserUuid.length
          ? fields?.underwriterUserUuid
          : 'null'
      );
      updateBrokerageData(
        'primaryPersonUuid',
        CreateBrokerageField.PrimaryContactUuid,
        fields?.primaryPersonUuid && fields?.primaryPersonUuid.length
          ? fields?.primaryPersonUuid
          : 'null'
      );

      try {
        const { data, errors } = await upsertEntity({
          variables: { entityInput: brokerageData },
        });

        brokerResponse = data;
        brokerResponseErrors = errors;

        notify(`Brokerage has been ${brokerageUuid ? 'updated' : 'added'}`, {
          variant: 'success',
        });
        if (brokerageUuid) {
          navigate.brokerage({
            brokerageUuid: brokerageUuid,
          });
        } else {
          navigate.producer();
        }
      } catch (cause) {
        console.error(
          `Failed to ${
            brokerageUuid ? 'update' : 'save'
          } brokerage. Please try again. (${cause})`
        );
        if (cause instanceof ApolloError) {
          brokerResponseErrors = cause.graphQLErrors;
        }
      }
      const errorMessage = brokerResponseErrors?.[0]?.message;
      if (errorMessage || !brokerResponse) {
        notify(
          `Failed to ${
            brokerageUuid ? 'update' : 'save'
          } brokerage. Please try again. (${
            errorMessage || 'No data returned by the API'
          })`,
          {
            variant: 'error',
          }
        );
        return;
      }
    },
    [
      upsertEntity,
      notify,
      navigate,
      isSameAddress,
      isLicenseBrokerage,
      brokerageUuid,
      brokerage,
      dirtyFields,
    ]
  );

  if (brokerageLoading && firstRender) {
    return <CircularLoader className="circular-loader-style" />;
  }

  if (brokerageUuid && !brokerage && !brokerageLoading) {
    return <p className="brokerage-message">Brokerage not found</p>;
  }

  const primaryBrokerageAddress = {
    primaryRegion: brokerage?.primaryState ?? '',
    primaryAddress1: brokerage?.primaryStreet1 ?? '',
    primaryAddress2: brokerage?.primaryStreet2 ?? '',
    primaryCity: brokerage?.primaryCity ?? '',
    primaryCountry: brokerage?.primaryCountry ?? '',
    primaryPostalCode: brokerage?.primaryPostalCode ?? '',
    primaryAddressUuid: brokerage?.primaryAddressUuid ?? '',
  };
  const isReadOnly = brokerage?.status === 'INACTIVE';
  return (
    <Container maxWidth={false} disableGutters>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          marginBottom: '38px',
          width: '96.5%',
        }}
        className={'brokerage-container'}
      >
        <BrokerBrokerageHeader
          active={!isReadOnly}
          showStatus={!!brokerageUuid}
          onArrowClick={() => navigate.producer()}
          value={value}
          uuid={brokerageUuid}
          name={formMethods.getValues(CreateBrokerageField.CompanyName)}
          onClickDeactivate={() => setOpen(true)}
          onClickReactivate={() => setReactiveOpen(true)}
        />
      </Box>
      <Box sx={{ width: '100%' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="simple tabs example"
          sx={{
            fontWeight: 'bold',
            color: '#000000 !important',

            borderBottom: '1px solid #000000',
            margin: '0 52px',
            '& .Mui-selected': {
              fontWeight: 'bold',
              color: '#000000 !important',
            },
          }}
          variant="fullWidth"
        >
          <Tab
            className="brokerage-tab"
            label="Brokerage Information"
            {...a11yProps(0)}
          />
          <Tab
            className="brokerage-tab"
            label="Brokerage Notes"
            {...a11yProps(1)}
          />
        </Tabs>
        <TabPanel value={value} index={0}>
          <Box sx={{ padding: '30px' }}>
            <Stack
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FormProvider {...formMethods}>
                <form className="form-wrapper-style">
                  <BrokageFormAccordions
                    setIsSameAddress={setIsSameAddress}
                    formMethods={formMethods}
                    setIsLicenseBrokerage={setIsLicenseBrokerage}
                    isSameAddress={isSameAddress}
                    onSubmitHandler={onSubmitHandlers}
                    brokerageUuid={brokerageUuid}
                    brokerageLicense={brokerage?.licenses || []}
                    brokerageTeams={brokerage?.teams || []}
                    brokers={brokerage?.listOfBrokers || []}
                    offices={transformOffices(brokerage?.offices)}
                    primaryBrokerageAddress={primaryBrokerageAddress}
                    isReadOnly={isReadOnly}
                    brokerageBillingStreet1={brokerage?.billingStreet1}
                    brokerageBillingStreet2={brokerage?.billingStreet2}
                    brokerageBillingCity={brokerage?.billingCity}
                    brokerageBillingPostalCode={brokerage?.billingPostalCode}
                    brokerageBillingCountry={brokerage?.billingCountry}
                    brokerageBillingRegion={brokerage?.billingState}
                    onLicenseSortChange={handleLicenseSortChange}
                    parentBrokerageName={brokerage?.parentBrokerage}
                    partnerBrokerageName={brokerage?.partnerName}
                    isLoading={brokerageLoading}
                  />
                </form>
              </FormProvider>
            </Stack>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={1}>
          {brokerageUuid ? (
            <BrokerageNoteContainer brokerageUuid={brokerageUuid} />
          ) : (
            <Box className="brokerage-message-container">
              Please save the brokerage information <br /> before including any
              notes.
            </Box>
          )}
        </TabPanel>
        <Box className="btn-container-2">
          <Box className="cancel-btn">
            <NewConvergeButton
              data-testid="brokerage-cancel"
              type="reset"
              label={'Cancel'}
              variant={'contained'}
              onClick={() => navigate.producer()}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  boxShadow: 'none',
                },
              }}
            />
          </Box>
          <Box className="add-btn">
            <NewConvergeButton
              data-testid="adding-brokerage"
              disabled={isReadOnly || !isDirty}
              type="submit"
              label={brokerageUuid ? 'Update Brokerage' : 'Save Brokerage'}
              loading={loading}
              onClick={() => {
                // First trigger form validation
                formMethods.handleSubmit(onSubmitHandlers())();

                // After validation, scroll to the first error field if any
                scrollToFirstError(formMethods);
              }}
            />
          </Box>
        </Box>

        <DeleteModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          isBrokerage
          entityUuid={brokerageUuid}
        />
        <ReactiveBrokerBrokerage
          open={reactiveOpen}
          onClose={() => {
            setReactiveOpen(false);
          }}
          isBrokerage
          entityUuid={brokerageUuid}
        />
      </Box>
    </Container>
  );
};

export const DeleteModal = ({
  onClose,
  entityUuid,
  personUuid,
  open,
  isBrokerage,
}: {
  onClose?: () => void;
  entityUuid?: string;
  open: boolean;
  personUuid?: string;
  isBrokerage?: boolean;
}) => {
  const { upsertEntity, loading } = useUpsertBrokerageMutation();
  const { notify } = useConvergeSnackBar();
  const navigate = useConvergeNavigation();
  const formMethods = useForm<CreateBrokerageFields>({
    defaultValues: {},
    mode: 'all',
  });
  const name = isBrokerage ? 'brokerage' : 'broker';

  const onSubmitStatusHandler = useCallback(
    (): SubmitHandler<CreateBrokerageFields> => async (fields) => {
      let brokerResponse, brokerResponseErrors;

      const brokerageData = {
        status: 'INACTIVE',
        entityUuid: entityUuid,
      } as any;

      const brokerData = {
        entityUuid: entityUuid,
        persons: [{ personUuid: personUuid, status: 'INACTIVE' }],
      } as any;

      const updateName = name.charAt(0).toUpperCase() + name.slice(1);

      try {
        const { data, errors } = await upsertEntity({
          variables: { entityInput: isBrokerage ? brokerageData : brokerData },
        });

        brokerResponse = data;
        brokerResponseErrors = errors;

        notify(`${updateName} has been deactivated`, {
          variant: 'success',
        });

        if (isBrokerage) {
          navigate.producer();
        } else {
          navigate.producerBrokers();
        }
      } catch (cause) {
        console.error(`Failed to update ${name}: ${cause}`);
        if (cause instanceof ApolloError) {
          brokerResponseErrors = cause.graphQLErrors;
        }
      }
      const errorMessage = brokerResponseErrors?.[0]?.message;
      if (errorMessage || !brokerResponse) {
        notify(
          `Failed to update ${name} : ${
            errorMessage || 'No data returned by the API'
          }`,
          {
            variant: 'error',
          }
        );
        return;
      }
    },
    [entityUuid, navigate, notify, upsertEntity, isBrokerage, name, personUuid]
  );

  return (
    <DialogLayout
      open={open}
      onClose={onClose}
      showCloseButton={true}
      onCloseIconClick={onClose}
      sx={{
        minWidth: '600px',
        '& .MuiPaper-root.MuiDialog-paper': {
          maxWidth: '600px',
          minWidth: '600px',
          overflow: 'hidden',
        },
      }}
      className="delete-modal"
      header={
        <Box className="modal-title-2">
          <Warning className="warning-icon" sx={{ marginRight: '8px' }} />
          <p
            style={{
              margin: 0,
              color: '#D32F2F',
              fontSize: '24px',
            }}
          >{`Deactivate ${capitalizeFirstLetter(name)}`}</p>
        </Box>
      }
    >
      <Stack>
        <Box>
          <Typography className="modal-message">
            Are you sure you want to deactivate this {`${name}`}?
          </Typography>
        </Box>
      </Stack>

      <Stack className="button-container-modal">
        <Box className="close-btn-Container">
          <NewConvergeButton
            label="CANCEL"
            variant="outlined"
            onClick={onClose}
            data-testid="cancel-brokerage-license-btn"
          />
        </Box>
        <Box>
          <NewConvergeButton
            loading={loading}
            onClick={formMethods.handleSubmit(onSubmitStatusHandler())}
            data-testid={`deactivate-${name}-btn`}
            label={`Deactivate ${name}`}
          />
        </Box>
      </Stack>
    </DialogLayout>
  );
};
